import * as React from 'react'
import cn from 'classnames'
import { X } from '@phosphor-icons/react'
import {
  CheckinCycle,
  CheckinCycleAttributes,
  CheckinCycleNotification,
  CheckinQuestionType,
  CheckinType,
  ParticipantScope,
  Recurrence,
} from './types'
import { recurrenceLabel, formatDayRange } from './utils'
import { useApiClient } from '../../utils/use-api-client'
import { successToast } from '../../utils/success-toast'
import { errorToast } from '../../utils/error-toast'
import { StepDots } from './step-dots'
import { StepOne, CHECKIN_TYPE_OPTIONS } from './step-one'
import { StepTwo, CycleWorkflow, OrgTeam, workflowLabel } from './step-two'
import { StepThree, CycleQuestion } from './step-three'
import { normalizeQuestionText } from 'components/checkin-questions'
import { StepFour } from './step-four'
import { NotificationModal } from 'components/checkin-notifications'
import { RatingScalesModal } from './rating-scales-modal'
import { RemoveConfirmDialog } from './remove-confirm-dialog'
import {
  NotificationDraft,
  draftFromSerialized,
  serializeNotification,
} from './notifications'
import {
  toTwentyFourHour,
  labelFromStoredTime,
  cycleNameError,
} from './form-helpers'

type StepKey = 'basic' | 'setup' | 'questions' | 'review'

// Short label shown after "Step X of N" in the header.
const STEP_LABELS: Record<StepKey, string> = {
  basic: 'Basic',
  setup: 'Setup',
  questions: 'Custom questions',
  review: 'Review',
}

type TeamsResponse = {
  data: Array<{
    id: string
    type: 'teams'
    attributes: { name: string; slug: string }
  }>
}

type CheckinQuestionsResponse = {
  data: Array<{
    id: string
    type: 'checkin_questions'
    attributes: {
      question_type: CheckinQuestionType
      body: string
      team_id: string | null
      position: number
    }
  }>
}

type CheckinCycleNotificationsResponse = {
  data: Array<{
    id: string
    type: 'checkin_cycle_notifications'
    attributes: Omit<CheckinCycleNotification, 'id'>
  }>
}

type CreateCycleResponse = {
  data: {
    id: string
    type: 'checkin_cycles'
    attributes: CheckinCycleAttributes
  }
}

// Map a cycle's serialized question snapshot into the builder's working shape.
// Working questions are keyed by numeric id (server ids are numeric strings); ones
// added in this flow get negative ids (see nextQuestionId).
const snapshotQuestions = (cycle: CheckinCycle): CycleQuestion[] =>
  cycle.questions.map((question) => ({
    id: Number(question.id),
    questionType: question.question_type,
    body: question.body,
    teamId: question.team_id,
  }))

type NewCycleModalProps = {
  orgSlug: string
  // When provided, the modal prefills its fields from this cycle. By default that
  // means edit mode (submitting PATCHes the cycle). When `asTemplate` is set, the
  // fields are still prefilled but submitting creates a brand-new cycle, leaving
  // the source cycle untouched.
  cycle?: CheckinCycle
  asTemplate?: boolean
  // Names already held by the org's other active cycles. A cycle name must be
  // unique among active cycles (CheckinCycle#name_unique_among_active_cycles),
  // and reusing an archived cycle as a template prefills its name — so without
  // this the second "Use as template" only failed at Create.
  takenNames?: string[]
  onClose: () => void
  onCreated: (cycle: CheckinCycle) => void
  onUpdated?: (cycle: CheckinCycle) => void
}

// Custom performance questions only apply to performance reviews. Snapshot the
// configured questions: org-wide defaults plus team-specific ones for teams
// actually participating in this cycle (the library may hold questions for
// other teams that the builder never surfaced). When editing a cycle that is
// no longer a performance review, send an empty set to clear the snapshot;
// on create, omit the key entirely.
const questionsAttribute = (
  checkinType: CheckinType,
  questions: CycleQuestion[],
  participatingTeams: OrgTeam[],
  isEditing: boolean,
) => {
  if (checkinType !== 'performance') {
    return isEditing ? [] : undefined
  }

  const participatingTeamIds = new Set(
    participatingTeams.map((team) => team.id),
  )
  return questions
    .filter(
      (question) =>
        question.teamId === null || participatingTeamIds.has(question.teamId),
    )
    .map((question) => ({
      question_type: question.questionType,
      body: question.body,
      team_id: question.teamId,
    }))
}

// Recurring cycles carry their own schedule (collected on Step 1), which is
// what the backend generates occurrences from. When editing a cycle back to
// one-time, clear the fields so a stale recurrence schedule can't linger; on
// create, omit them entirely.
const recurrenceAttributes = (
  recurrence: Recurrence,
  recStartDate: string,
  recEndDate: string,
  recStartTime: string,
  recTimezone: string,
  isEditing: boolean,
) => {
  if (recurrence !== 'one_time') {
    return {
      rec_start_date: recStartDate,
      rec_end_date: recEndDate,
      rec_start_time: toTwentyFourHour(recStartTime),
      rec_timezone: recTimezone,
    }
  }
  return isEditing
    ? {
        rec_start_date: null,
        rec_end_date: null,
        rec_start_time: null,
        rec_timezone: null,
      }
    : {}
}

// team_ids is sent when team-scoped; when editing, an empty set is sent for a
// whole-org cycle so any previously attached teams are detached. On create,
// omit the key entirely for a whole-org cycle.
const teamIdsAttribute = (
  scope: ParticipantScope,
  selectedTeamIds: string[],
  isEditing: boolean,
) => {
  if (scope === 'teams') return selectedTeamIds
  return isEditing ? [] : undefined
}

// Escape closes the wizard, but not while a modal-over-modal (notification
// editor, remove-confirmation dialog, rating scales preview) owns Escape itself.
// A nested Radix layer (QuestionRow's remove-confirm dialog / kebab menu)
// preventDefaults the Escape it handles, so this bails if it already consumed it.
function useEscapeToClose(onClose: () => void, blocked: boolean) {
  React.useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented) return
      if (event.key === 'Escape' && !blocked) onClose()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [onClose, blocked])
}

function useOrgTeams(apiClient: ReturnType<typeof useApiClient>, orgSlug: string) {
  const [teams, setTeams] = React.useState<OrgTeam[]>([])
  const [teamsLoading, setTeamsLoading] = React.useState(true)
  const [teamsLoadError, setTeamsLoadError] = React.useState<string | null>(
    null,
  )

  React.useEffect(() => {
    let cancelled = false

    const loadTeams = async () => {
      setTeamsLoading(true)
      setTeamsLoadError(null)

      try {
        const response = await apiClient.get<TeamsResponse>(
          `/api/v1/internal/orgs/${orgSlug}/teams`,
        )
        if (cancelled) return

        setTeams(
          (response?.data ?? []).map((resource) => ({
            id: resource.id,
            name: resource.attributes.name,
          })),
        )
      } catch (err: unknown) {
        if (cancelled) return
        const message =
          err instanceof Error ? err.message : 'Failed to load teams.'
        setTeamsLoadError(message)
      } finally {
        if (!cancelled) setTeamsLoading(false)
      }
    }

    loadTeams()

    return () => {
      cancelled = true
    }
  }, [apiClient, orgSlug])

  return { teams, teamsLoading, teamsLoadError }
}

// Pre-load the org's custom-question library (org-wide defaults + team-specific
// overrides) so Step 3 opens populated. Questions the admin adds in this flow are
// appended to local state and persisted with the cycle. Skipped when editing —
// the cycle's saved snapshot is used instead.
function useLoadInitialQuestions(
  apiClient: ReturnType<typeof useApiClient>,
  orgSlug: string,
  cycle: CheckinCycle | undefined,
  setQuestions: React.Dispatch<React.SetStateAction<CycleQuestion[]>>,
  setQuestionsLoading: React.Dispatch<React.SetStateAction<boolean>>,
  setQuestionsLoadError: React.Dispatch<React.SetStateAction<string | null>>,
) {
  React.useEffect(() => {
    if (cycle) return undefined

    let cancelled = false

    const loadQuestions = async () => {
      setQuestionsLoading(true)
      setQuestionsLoadError(null)

      try {
        const response = await apiClient.get<CheckinQuestionsResponse>(
          `/api/v1/internal/orgs/${orgSlug}/checkin_questions`,
        )
        if (cancelled) return

        setQuestions(
          (response?.data ?? []).map((resource) => ({
            id: Number(resource.id),
            questionType: resource.attributes.question_type,
            body: resource.attributes.body,
            teamId: resource.attributes.team_id,
          })),
        )
      } catch (err: unknown) {
        if (cancelled) return
        const message =
          err instanceof Error ? err.message : 'Failed to load questions.'
        setQuestionsLoadError(message)
      } finally {
        if (!cancelled) setQuestionsLoading(false)
      }
    }

    loadQuestions()

    return () => {
      cancelled = true
    }
  }, [
    apiClient,
    orgSlug,
    cycle,
    setQuestions,
    setQuestionsLoading,
    setQuestionsLoadError,
  ])
}

// Seed a NEW cycle's reminder cadence from the org's configured defaults (the
// same org-level rows the Performance Cycle page manages). Reminders the admin
// edits here are persisted with the cycle. Skipped when editing — the cycle's
// own saved snapshot is used instead.
function useLoadDefaultNotifications(
  apiClient: ReturnType<typeof useApiClient>,
  orgSlug: string,
  cycle: CheckinCycle | undefined,
  setNotifications: React.Dispatch<React.SetStateAction<NotificationDraft[]>>,
) {
  React.useEffect(() => {
    if (cycle) return undefined

    let cancelled = false

    const loadNotifications = async () => {
      try {
        const response = await apiClient.get<CheckinCycleNotificationsResponse>(
          `/api/v1/internal/orgs/${orgSlug}/checkin_cycle_notifications`,
        )
        if (cancelled) return

        // Only reminders the admin has toggled ON at the org level pre-populate a
        // new cycle; disabled org defaults are intentionally left out.
        setNotifications(
          (response?.data ?? [])
            .filter((resource) => resource.attributes.enabled)
            .map((resource) =>
              draftFromSerialized({ id: resource.id, ...resource.attributes }),
            ),
        )
      } catch (err: unknown) {
        if (cancelled) return
        errorToast(
          err instanceof Error ? err.message : 'Failed to load reminders.',
        )
      }
    }

    loadNotifications()

    return () => {
      cancelled = true
    }
  }, [apiClient, orgSlug, cycle, setNotifications])
}

type NewCycleModalBodyProps = {
  editingNotification: NotificationDraft | 'new' | null
  notifications: NotificationDraft[]
  onSaveNotification: (draft: NotificationDraft) => void
  onCloseNotificationEditor: () => void
  viewingRatingScales: boolean
  orgSlug: string
  onCloseRatingScales: () => void
  onClose: () => void
  isEditing: boolean
  currentStep: number
  totalSteps: number
  currentKey: StepKey
  stepOneProps: React.ComponentProps<typeof StepOne>
  stepTwoProps: React.ComponentProps<typeof StepTwo>
  stepThreeProps: React.ComponentProps<typeof StepThree>
  stepFourProps: React.ComponentProps<typeof StepFour>
  onBack: () => void
  onSubmit: () => void
  submitting: boolean
  submitButtonLabel: string
  continueEnabled: boolean
  onContinue: () => void
  notificationPendingRemoval: string | null
  onCancelRemoveNotification: () => void
  onConfirmRemoveNotification: () => void
}

// The wizard's rendering, pulled out of NewCycleModal so the render tree (which
// picks the notification editor, the rating-scales preview, or one of the four
// wizard steps) is its own unit rather than folded into the state/validation
// logic above it.
const NewCycleModalBody = ({
  editingNotification,
  notifications,
  onSaveNotification,
  onCloseNotificationEditor,
  viewingRatingScales,
  orgSlug,
  onCloseRatingScales,
  onClose,
  isEditing,
  currentStep,
  totalSteps,
  currentKey,
  stepOneProps,
  stepTwoProps,
  stepThreeProps,
  stepFourProps,
  onBack,
  onSubmit,
  submitting,
  submitButtonLabel,
  continueEnabled,
  onContinue,
  notificationPendingRemoval,
  onCancelRemoveNotification,
  onConfirmRemoveNotification,
}: NewCycleModalBodyProps) => {
  // The notification editor replaces the wizard rather than stacking over it:
  // the wizard stays mounted (so every field keeps its value), but while a
  // notification is being added/edited only the editor renders. Closing it
  // returns to the wizard exactly as it was left.
  if (editingNotification !== null) {
    return (
      <NotificationModal
        notification={
          editingNotification === 'new' ? null : editingNotification
        }
        existingNames={notifications
          .filter(
            (n) =>
              editingNotification === 'new' || n.id !== editingNotification.id,
          )
          .map((n) => n.name)}
        onSave={onSaveNotification}
        onClose={onCloseNotificationEditor}
      />
    )
  }

  // Same replace-not-stack strategy: the rating-scales preview takes over the
  // overlay while open, leaving every wizard field untouched underneath.
  if (viewingRatingScales) {
    return <RatingScalesModal orgSlug={orgSlug} onClose={onCloseRatingScales} />
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-[rgba(29,38,74,0.22)] px-4 py-4">
      <div className="flex h-[689px] max-h-full w-full max-w-[628px] flex-col rounded-lg bg-white p-5 shadow-[0_18px_44px_rgba(29,38,74,0.18)] outline outline-1 outline-offset-[-1px] outline-gray-200">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="font-talent text-[23px] font-medium leading-[28px] text-[#1D264A]">
              {isEditing ? 'Edit check-in cycle' : 'New check-in cycle'}
            </h2>
            <p className="mb-0 mt-1 font-talent text-[16px] font-normal leading-[20px] text-[#9aa1ba]">
              Step {currentStep} of {totalSteps} &middot;{' '}
              {STEP_LABELS[currentKey]}
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="inline-flex h-[40px] w-[40px] shrink-0 items-center justify-center rounded-full bg-[#EFF1F8] text-[#5E6580] hover:bg-[#E3E8F1]"
          >
            <X size={20} />
          </button>
        </div>

        <div className="my-4 h-px w-full bg-[#E7EAF4]" />

        {/* Full-bleed (-mx-5) then re-padded (px-5) so inputs' focus rings have
            room inside the scroll area instead of being clipped left/right by the
            overflow. min-h-0 lets this flex child scroll within the fixed height. */}
        <div className="-mx-5 min-h-0 flex-1 overflow-y-auto px-5">
          {currentKey === 'basic' && <StepOne {...stepOneProps} />}
          {currentKey === 'setup' && <StepTwo {...stepTwoProps} />}
          {currentKey === 'questions' && <StepThree {...stepThreeProps} />}
          {currentKey === 'review' && <StepFour {...stepFourProps} />}
        </div>

        <div className="my-4 h-px w-full bg-[#E7EAF4]" />

        <div className="flex items-center justify-between gap-4">
          <StepDots totalSteps={totalSteps} currentStep={currentStep} />

          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={onClose}
              className="inline-flex items-center justify-center px-[14px] py-2 text-center font-talent text-[16px] font-medium leading-[20px] text-[#5E6580] hover:text-[#1D264A]"
            >
              Cancel
            </button>

            {currentStep > 1 && (
              <button
                type="button"
                onClick={onBack}
                className="inline-flex items-center justify-center rounded-lg border border-solid border-[#D7DBE8] bg-white px-[14px] py-2 text-center font-talent text-[16px] font-medium leading-[20px] text-[#1D264A] hover:bg-[#F7F8FB]"
              >
                Back
              </button>
            )}

            {currentStep === totalSteps ? (
              <button
                type="button"
                onClick={onSubmit}
                disabled={submitting}
                className={cn(
                  'inline-flex items-center justify-center rounded-lg px-[14px] py-2 text-center font-talent text-[16px] font-medium leading-[20px] text-white transition-opacity',
                  submitting
                    ? 'bg-[#2E9E5D] opacity-50'
                    : 'bg-[#2E9E5D] hover:bg-[#268650]',
                )}
              >
                {submitButtonLabel}
              </button>
            ) : (
              <button
                type="button"
                onClick={onContinue}
                aria-disabled={!continueEnabled}
                className={cn(
                  'inline-flex items-center justify-center rounded-lg px-[14px] py-2 text-center font-talent text-[16px] font-medium leading-[20px] text-white transition-opacity',
                  continueEnabled
                    ? 'bg-[#2E9E5D] hover:bg-[#268650]'
                    : 'bg-[#2E9E5D] opacity-50',
                )}
              >
                Continue
              </button>
            )}
          </div>
        </div>
      </div>

      {notificationPendingRemoval !== null && (
        <RemoveConfirmDialog
          title="Are you sure you want to remove this notification?"
          description="Once the notification is removed, you can not undo the action."
          onCancel={onCancelRemoveNotification}
          onConfirm={onConfirmRemoveNotification}
        />
      )}
    </div>
  )
}

export const NewCycleModal: React.FC<NewCycleModalProps> = ({
  orgSlug,
  cycle,
  asTemplate = false,
  takenNames = [],
  onClose,
  onCreated,
  onUpdated,
}) => {
  const apiClient = useApiClient()
  // Prefilling from a cycle as a template still produces a new cycle, so it is
  // not "editing" — only an existing cycle opened for edit PATCHes.
  const isEditing = Boolean(cycle) && !asTemplate
  // Step 1 — Basic
  const [name, setName] = React.useState(cycle?.name ?? '')
  const [startDate, setStartDate] = React.useState(cycle?.start_date ?? '')
  const [endDate, setEndDate] = React.useState(cycle?.end_date ?? '')
  const [startTime, setStartTime] = React.useState(
    cycle ? labelFromStoredTime(cycle.start_time) : '9 AM',
  )
  const [timezone, setTimezone] = React.useState(
    cycle?.timezone ?? 'America/New_York',
  )
  const [recurrence, setRecurrence] = React.useState<Recurrence>(
    cycle?.recurrence ?? 'one_time',
  )
  // Recurrence schedule — only collected (and sent) when recurrence !== one_time.
  const [recStartDate, setRecStartDate] = React.useState(
    cycle?.rec_start_date ?? '',
  )
  const [recEndDate, setRecEndDate] = React.useState(cycle?.rec_end_date ?? '')
  const [recStartTime, setRecStartTime] = React.useState(
    cycle ? labelFromStoredTime(cycle.rec_start_time) : '9 AM',
  )
  const [recTimezone, setRecTimezone] = React.useState(
    cycle?.rec_timezone ?? 'America/New_York',
  )
  const [checkinType, setCheckinType] = React.useState<CheckinType>(
    cycle?.checkin_type ?? 'standard',
  )

  // Step 2 — Setup
  const [scope, setScope] = React.useState<ParticipantScope>(
    cycle?.participant_scope ?? 'whole_org',
  )
  const [selectedTeamIds, setSelectedTeamIds] = React.useState<string[]>(
    cycle ? cycle.team_ids.map(String) : [],
  )
  const [workflow, setWorkflow] = React.useState<CycleWorkflow>(
    cycle?.workflow ?? 'default',
  )
  // Notification cadence. A new cycle is seeded from the org's configured
  // defaults (fetched below, managed on the Performance Cycle page); editing an
  // existing cycle seeds from its own saved snapshot.
  const [notifications, setNotifications] = React.useState<NotificationDraft[]>(
    () => (cycle ? cycle.notifications.map(draftFromSerialized) : []),
  )
  // The notification being added (null sentinel via 'new') or edited. While set,
  // the standalone NotificationModal replaces the wizard (see render below).
  const [editingNotification, setEditingNotification] = React.useState<
    NotificationDraft | 'new' | null
  >(null)
  // Monotonic counter for client-side ids of notifications added in this flow.
  const nextNotificationId = React.useRef(0)
  // The notification awaiting delete confirmation (id), or null when no dialog.
  const [notificationPendingRemoval, setNotificationPendingRemoval] =
    React.useState<string | null>(null)
  // When true, the read-only rating-scales preview replaces the wizard (see the
  // render below), same as the notification editor.
  const [viewingRatingScales, setViewingRatingScales] = React.useState(false)

  const { teams, teamsLoading, teamsLoadError } = useOrgTeams(
    apiClient,
    orgSlug,
  )

  // Step 3 — Custom questions. When editing, seed from the cycle's own snapshot
  // (not the org library) so the review reflects what was saved.
  const [instructions, setInstructions] = React.useState(
    cycle?.instructions ?? '',
  )
  const [questions, setQuestions] = React.useState<CycleQuestion[]>(
    cycle ? snapshotQuestions(cycle) : [],
  )
  const [questionsLoading, setQuestionsLoading] = React.useState(!cycle)
  const [questionsLoadError, setQuestionsLoadError] = React.useState<
    string | null
  >(null)
  // Decrementing counter for the client-side (negative) ids of questions added in
  // this flow; ids are stripped on submit — the whole set is snapshotted with the
  // cycle.
  const nextQuestionId = React.useRef(0)

  const [currentStep, setCurrentStep] = React.useState(1)
  const [submitAttempted, setSubmitAttempted] = React.useState(false)
  const [submitting, setSubmitting] = React.useState(false)

  // The custom-questions step is only part of the flow for performance reviews;
  // standard check-ins skip straight from Setup to Review. checkinType can only
  // change on step 1, so currentStep stays within range as the list resizes.
  const steps: StepKey[] =
    checkinType === 'performance'
      ? ['basic', 'setup', 'questions', 'review']
      : ['basic', 'setup', 'review']
  const totalSteps = steps.length
  const currentKey = steps[currentStep - 1]

  useEscapeToClose(
    onClose,
    editingNotification !== null ||
      notificationPendingRemoval !== null ||
      viewingRatingScales,
  )

  useLoadInitialQuestions(
    apiClient,
    orgSlug,
    cycle,
    setQuestions,
    setQuestionsLoading,
    setQuestionsLoadError,
  )

  useLoadDefaultNotifications(apiClient, orgSlug, cycle, setNotifications)

  // --- Step 1 validation -----------------------------------------------------
  // The date-order error surfaces as soon as both dates are chosen (independent
  // of a Continue attempt); empty-required errors surface only after Continue.
  const dateOrderInvalid = Boolean(startDate && endDate && endDate < startDate)

  // Recurring cycles add their own schedule, required and order-checked the same
  // way as the base schedule. The recurrence dates must also fall within the base
  // cycle window (start_date … end_date).
  const isRecurring = recurrence !== 'one_time'
  const recDateOrderInvalid = Boolean(
    recStartDate && recEndDate && recEndDate < recStartDate,
  )
  const outsideBaseRange = (value: string): boolean =>
    Boolean(
      value &&
      ((startDate && value < startDate) || (endDate && value > endDate)),
    )
  const recStartOutOfRange = outsideBaseRange(recStartDate)
  const recEndOutOfRange = outsideBaseRange(recEndDate)
  const recurrenceValid =
    !isRecurring ||
    (recStartDate !== '' &&
      recEndDate !== '' &&
      recStartTime !== '' &&
      recTimezone !== '' &&
      !recDateOrderInvalid &&
      !recStartOutOfRange &&
      !recEndOutOfRange)

  // Gate Continue on the same rules the inline error reports, passing
  // submitAttempted: true so a blank name counts as invalid from the start.
  const nameValid = cycleNameError(name, true, takenNames) === null

  const step1Valid =
    nameValid &&
    startDate !== '' &&
    endDate !== '' &&
    startTime !== '' &&
    !dateOrderInvalid &&
    recurrenceValid

  const nameError = cycleNameError(name, submitAttempted, takenNames)
  const startDateError =
    submitAttempted && !startDate ? 'Start date is required' : null
  let endDateError: string | null = null
  if (!endDate) {
    endDateError = submitAttempted ? 'End date is required' : null
  } else if (dateOrderInvalid) {
    endDateError = 'End date must be after the start date'
  }
  const startTimeError =
    submitAttempted && !startTime ? 'Start time is required' : null

  let recStartDateError: string | null = null
  if (isRecurring) {
    if (recStartOutOfRange) {
      recStartDateError = 'Must be within the cycle start and end dates'
    } else if (submitAttempted && !recStartDate) {
      recStartDateError = 'Recurrence start date is required'
    }
  }

  let recEndDateError: string | null = null
  if (isRecurring) {
    if (recEndOutOfRange) {
      recEndDateError = 'Must be within the cycle start and end dates'
    } else if (!recEndDate) {
      recEndDateError = submitAttempted
        ? 'Recurrence end date is required'
        : null
    } else if (recDateOrderInvalid) {
      recEndDateError = 'End date must be after the start date'
    }
  }
  const recStartTimeError =
    submitAttempted && isRecurring && !recStartTime
      ? 'Recurrence start time is required'
      : null

  // --- Step 2 validation -----------------------------------------------------
  // Whole-organisation is always valid; "Specific teams" needs at least one team.
  const step2Valid = scope === 'whole_org' || selectedTeamIds.length > 0
  const teamsError =
    submitAttempted && scope === 'teams' && selectedTeamIds.length === 0
      ? 'Please select at least one team'
      : null

  // The questions/review steps impose no required fields — Continue is always
  // enabled there.
  let continueEnabled = true
  if (currentKey === 'basic') {
    continueEnabled = step1Valid
  } else if (currentKey === 'setup') {
    continueEnabled = step2Valid
  }

  const toggleTeam = (teamId: string) =>
    setSelectedTeamIds((current) =>
      current.includes(teamId)
        ? current.filter((value) => value !== teamId)
        : [...current, teamId],
    )

  // Teams whose team-specific questions can be configured: the selected
  // participating teams when team-scoped, otherwise every org team.
  const participatingTeams =
    scope === 'teams'
      ? teams.filter((team) => selectedTeamIds.includes(team.id))
      : teams

  // Throws if any target (default, or a selected team) already has a question
  // with the same visible text as `body` — mirrors the org-wide question
  // library's uniqueness validation, which this wizard's local working state
  // otherwise bypasses entirely. Shared by add and edit so editing a question
  // into a duplicate is rejected the same as creating one; `excludeId` skips
  // the row being edited itself. teamIds is [null] for an org-wide default, or
  // one entry per selected/owning team.
  const assertNoDuplicateQuestion = (
    body: string,
    teamIds: (string | null)[],
    excludeId?: number,
  ) => {
    const normalizedBody = normalizeQuestionText(body)
    const duplicateTeamIds = teamIds.filter((teamId) =>
      questions.some(
        (question) =>
          question.id !== excludeId &&
          question.teamId === teamId &&
          normalizeQuestionText(question.body) === normalizedBody,
      ),
    )

    if (duplicateTeamIds.length === 0) return

    const scopeLabels = [
      duplicateTeamIds.includes(null) ? 'the default questions' : null,
      ...duplicateTeamIds
        .filter((teamId): teamId is string => teamId !== null)
        .map((teamId) => teams.find((team) => team.id === teamId)?.name)
        .filter((name): name is string => Boolean(name)),
    ].filter(Boolean)

    throw new Error(
      `This question already exists for ${scopeLabels.join(', ')}.`,
    )
  }

  // Append one working question per target team. Ids are assigned outside the
  // state updater so a StrictMode double-invoke can't skip/duplicate the counter.
  const handleAddQuestion = ({
    questionType,
    body,
    teamIds,
  }: {
    questionType: CheckinQuestionType
    body: string
    teamIds: (string | null)[]
  }) => {
    assertNoDuplicateQuestion(body, teamIds)

    const additions = teamIds.map((teamId) => {
      nextQuestionId.current -= 1
      return { id: nextQuestionId.current, questionType, body, teamId }
    })
    setQuestions((current) => [...current, ...additions])
  }

  const handleUpdateQuestion = (id: number, body: string) => {
    const existing = questions.find((question) => question.id === id)
    assertNoDuplicateQuestion(body, [existing?.teamId ?? null], id)

    setQuestions((current) =>
      current.map((question) =>
        question.id === id ? { ...question, body } : question,
      ),
    )
  }

  // QuestionRow owns its own remove-confirmation dialog, so this fires only after
  // the admin confirms.
  const handleDeleteQuestion = (id: number) =>
    setQuestions((current) => current.filter((question) => question.id !== id))

  const toggleNotification = (id: string) =>
    setNotifications((current) =>
      current.map((notification) =>
        notification.id === id
          ? { ...notification, enabled: !notification.enabled }
          : notification,
      ),
    )

  // Removal is irreversible, so ask first — the actual filter happens in
  // confirmRemoveNotification once the dialog is confirmed.
  const removeNotification = (id: string) => setNotificationPendingRemoval(id)

  const confirmRemoveNotification = () => {
    const id = notificationPendingRemoval
    const removed = notifications.find((notification) => notification.id === id)
    setNotifications((current) =>
      current.filter((notification) => notification.id !== id),
    )
    setNotificationPendingRemoval(null)
    successToast(
      removed?.name ? `${removed.name} reminder removed` : 'Reminder removed',
    )
  }

  // Persist the draft from the editor: update the matching row in place, or
  // append a new one with a client-side id. Then close the editor.
  const handleSaveNotification = (draft: NotificationDraft) => {
    setNotifications((current) => {
      if (draft.id && current.some((item) => item.id === draft.id)) {
        return current.map((item) => (item.id === draft.id ? draft : item))
      }
      nextNotificationId.current += 1
      return [...current, { ...draft, id: `new-${nextNotificationId.current}` }]
    })
    setEditingNotification(null)
  }

  // --- Review (Step 4) display labels ----------------------------------------
  const checkinTypeLabel =
    CHECKIN_TYPE_OPTIONS.find((option) => option.value === checkinType)
      ?.title ?? checkinType

  const participantsLabel =
    scope === 'whole_org'
      ? 'Whole organisation'
      : participatingTeams.map((team) => team.name).join(', ') || '—'

  // Create (POST) or, in edit mode, update (PATCH) the assembled cycle, then
  // surface success and hand the saved cycle back to the page.
  const handleSubmit = async () => {
    if (submitting) return
    setSubmitting(true)

    const startTime24 = toTwentyFourHour(startTime)
    const attributes: Record<string, unknown> = {
      name: name.trim(),
      recurrence,
      participant_scope: scope,
      checkin_type: checkinType,
      workflow,
      start_date: startDate,
      end_date: endDate,
      start_time: startTime24,
      timezone,
    }

    // On edit, always send instructions so they can be cleared; on create, only
    // send when present.
    if (isEditing || instructions.trim() !== '') {
      attributes.instructions = instructions
    }

    attributes.questions = questionsAttribute(
      checkinType,
      questions,
      participatingTeams,
      isEditing,
    )

    Object.assign(
      attributes,
      recurrenceAttributes(
        recurrence,
        recStartDate,
        recEndDate,
        recStartTime,
        recTimezone,
        isEditing,
      ),
    )

    attributes.team_ids = teamIdsAttribute(scope, selectedTeamIds, isEditing)

    // The full notification cadence is always sent. The backend reconciles by id
    // (updating, creating, or removing rows) so delivery logs survive edits.
    attributes.notifications = notifications.map((notification, index) =>
      serializeNotification(notification, index, timezone),
    )

    const payload = { data: { type: 'checkin_cycles', attributes } }

    try {
      const response =
        isEditing && cycle
          ? await apiClient.patch<CreateCycleResponse>(
              `/api/v1/internal/orgs/${orgSlug}/checkin_cycles/${cycle.slug}`,
              payload,
            )
          : await apiClient.post<CreateCycleResponse>(
              `/api/v1/internal/orgs/${orgSlug}/checkin_cycles`,
              payload,
            )

      const saved: CheckinCycle = {
        id: response.data.id,
        ...response.data.attributes,
      }

      if (isEditing) {
        successToast('Cycle has been updated')
        onUpdated?.(saved)
      } else {
        successToast(`${saved.name} cycle has been added`)
        onCreated(saved)
      }
      setSubmitting(false)
      onClose()
    } catch (err: unknown) {
      errorToast(err instanceof Error ? err.message : undefined)
      setSubmitting(false)
    }
  }

  const handleContinue = () => {
    setSubmitAttempted(true)

    if (currentKey === 'basic') {
      if (!step1Valid) return
      setSubmitAttempted(false)
      setCurrentStep((step) => step + 1)
      return
    }

    if (currentKey === 'setup') {
      if (!step2Valid) return
      setSubmitAttempted(false)
      setCurrentStep((step) => step + 1)
      return
    }

    if (currentKey === 'questions') {
      // No required fields on the questions step; advance to the review step.
      setSubmitAttempted(false)
      setCurrentStep((step) => step + 1)
    }
    // The review step submits via handleSubmit, not Continue.
  }

  const handleBack = () => {
    setSubmitAttempted(false)
    setCurrentStep((step) => Math.max(1, step - 1))
  }

  let submitButtonLabel: string
  if (submitting) {
    submitButtonLabel = isEditing ? 'Updating…' : 'Creating…'
  } else {
    submitButtonLabel = isEditing ? 'Update Cycle' : 'Create cycle'
  }

  return (
    <NewCycleModalBody
      editingNotification={editingNotification}
      notifications={notifications}
      onSaveNotification={handleSaveNotification}
      onCloseNotificationEditor={() => setEditingNotification(null)}
      viewingRatingScales={viewingRatingScales}
      orgSlug={orgSlug}
      onCloseRatingScales={() => setViewingRatingScales(false)}
      onClose={onClose}
      isEditing={isEditing}
      currentStep={currentStep}
      totalSteps={totalSteps}
      currentKey={currentKey}
      stepOneProps={{
        name,
        onNameChange: setName,
        nameError,
        startDate,
        onStartDateChange: setStartDate,
        startDateError,
        endDate,
        onEndDateChange: setEndDate,
        endDateError,
        startTime,
        onStartTimeChange: setStartTime,
        startTimeError,
        timezone,
        onTimezoneChange: setTimezone,
        recurrence,
        onRecurrenceChange: setRecurrence,
        recStartDate,
        onRecStartDateChange: setRecStartDate,
        recStartDateError,
        recEndDate,
        onRecEndDateChange: setRecEndDate,
        recEndDateError,
        recStartTime,
        onRecStartTimeChange: setRecStartTime,
        recStartTimeError,
        recTimezone,
        onRecTimezoneChange: setRecTimezone,
        checkinType,
        onCheckinTypeChange: setCheckinType,
      }}
      stepTwoProps={{
        scope,
        onScopeChange: setScope,
        teams,
        teamsLoading,
        teamsLoadError,
        selectedTeamIds,
        onToggleTeam: toggleTeam,
        workflow,
        onWorkflowChange: setWorkflow,
        teamsError,
        notifications,
        onToggleNotification: toggleNotification,
        onEditNotification: setEditingNotification,
        onRemoveNotification: removeNotification,
        onAddNotification: () => setEditingNotification('new'),
      }}
      stepThreeProps={{
        instructions,
        onInstructionsChange: setInstructions,
        questions,
        onAddQuestion: handleAddQuestion,
        onUpdateQuestion: handleUpdateQuestion,
        onDeleteQuestion: handleDeleteQuestion,
        questionsLoading,
        questionsLoadError,
        participatingTeams,
        orgSlug,
        onViewRatingScales: () => setViewingRatingScales(true),
      }}
      stepFourProps={{
        name: name.trim(),
        checkinTypeLabel,
        dateRangeLabel: formatDayRange(startDate, endDate),
        recurrenceLabel: recurrenceLabel(recurrence),
        participantsLabel,
        workflowLabel: workflowLabel(workflow),
      }}
      onBack={handleBack}
      onSubmit={handleSubmit}
      submitting={submitting}
      submitButtonLabel={submitButtonLabel}
      continueEnabled={continueEnabled}
      onContinue={handleContinue}
      notificationPendingRemoval={notificationPendingRemoval}
      onCancelRemoveNotification={() => setNotificationPendingRemoval(null)}
      onConfirmRemoveNotification={confirmRemoveNotification}
    />
  )
}
